import { copyToClipboard } from '@repo/common-utils';
import { useUserEventLogger } from '@repo/tracking-service';
import { BottomDrawer, Button, Description, List, useToast } from '@zenterprise-inc/ui';
import { useEffect } from 'react';

import { useSummaryModal } from '@/components/tax-refund/common/context/SummaryModalProvider';
import { ModalProps, VirtualAccountInfo } from '@/lib/constants/summary';
import { zCurrency } from '@/lib/utils/zCurrency';

export const PaymentGuideModal = ({
  isOpen,
  onClose,
  sectionType,
  virtualAccountInfo
}: ModalProps & {
  sectionType: string;
  virtualAccountInfo: VirtualAccountInfo;
}) => {
  const { openModal } = useSummaryModal();
  const { sendViewEvent } = useUserEventLogger();

  useEffect(() => {
    if (isOpen) {
      sendViewEvent('home_modal_payment-how', {
        type: sectionType
      });
    }
  }, [isOpen, sectionType, sendViewEvent]);

  return (
    <BottomDrawer.Basic isVisible={isOpen} onCloseDrawer={onClose} backDropClose={false}>
      <BottomDrawer.Basic.Header isCloseButton onCloseDrawer={onClose}>
        어떤 방법으로 이용료를 결제할까요?
      </BottomDrawer.Basic.Header>
      <BottomDrawer.Basic.Contents isBottomButton>
        <div className={'px-6'}>
          <Description.Contents className={'mb-5 mt-3 text-gray-800'}>
            제휴 세무사 비용은 {zCurrency(virtualAccountInfo?.paymentAmount, '원', true, '0')} 입니다. 결제는 부가세가 포함된 금액으로 요청돼요.
          </Description.Contents>
          <List.Basic
            className={'cursor-pointer'}
            left={<img src="/images/illus-icons/fill-account-rg.svg" alt="account icon" />}
            title={{ text: '계좌 입금으로 결제하기', titleStyle: 'font-bold' }}
            subText={{ text: '계좌 입금 완료되면 알림톡으로 현금영수증을 발급 정보를 전달해드려요.' }}
            listIcon
            onClick={() => {
              onClose();
              openModal('PaymentAccountModal', virtualAccountInfo);
            }}
          />
          <List.Basic
            className={'cursor-pointer'}
            left={<img src="/images/illus-icons/fill-card-rg.svg" alt="card icon" />}
            title={{ text: '카드로 결제하기', titleStyle: 'font-bold' }}
            subText={{ text: '결제 금액이 200,000원 이상인 경우 할부 결제가 가능해요.' }}
            listIcon
          />
        </div>
      </BottomDrawer.Basic.Contents>
      <BottomDrawer.Basic.BottomButton>
        <Button.StickyBottom.Button className="w-full" state="primary" onClick={onClose}>
          확인
        </Button.StickyBottom.Button>
      </BottomDrawer.Basic.BottomButton>
    </BottomDrawer.Basic>
  );
};

export const PaymentAccountModal = ({
  isOpen,
  onClose,
  virtualAccountInfo,
  sectionType
}: ModalProps & {
  sectionType: string;
  virtualAccountInfo: VirtualAccountInfo;
}) => {
  const toast = useToast();
  const { sendViewEvent, sendClickEvent } = useUserEventLogger();

  useEffect(() => {
    if (isOpen) {
      sendViewEvent('home_modal_account-guide', {
        type: sectionType
      });
    }
  }, [isOpen, sectionType, sendViewEvent]);

  return (
    <BottomDrawer.Basic isVisible={isOpen} onCloseDrawer={onClose} backDropClose={false}>
      <BottomDrawer.Basic.Header isCloseButton onCloseDrawer={onClose} />
      <BottomDrawer.Basic.Contents>
        <div className={'px-6 pb-4 pt-3'}>
          <Description.Contents className={'text-18 mb-5 mt-3 text-center text-gray-800'}>
            <span className={'font-bold'}>‘{virtualAccountInfo?.owner}’</span>에
            <br />
            <span className={'font-bold'}>{zCurrency(virtualAccountInfo?.paymentAmount, '원', true, '0')}</span>을 입금해주세요.
          </Description.Contents>
          <List.Basic
            left={<img src="/images/symbols/ibk-logo.svg" alt="card icon" />}
            overText={{ text: virtualAccountInfo?.bank ?? 'IBK 기업은행', color: 'gray.700' }}
            title={{ text: virtualAccountInfo?.account, titleStyle: 'font-bold' }}
            right={{
              type: 'button',
              component: (
                <Button.Box
                  state="basic"
                  onClick={() => {
                    sendClickEvent('home_modal_account-num-copy');
                    copyToClipboard(virtualAccountInfo.account.replace(/-/g, ''));
                    toast({ content: '계좌번호를 복사했어요.', icon: 'check-mark', iconColor: 'green.600' });
                  }}
                >
                  복사
                </Button.Box>
              )
            }}
          />
          <Description.Feedback
            text={'토스, 카카오뱅크 등 간편송금 서비스에서 입금할 경우, 예금주명이 보이지 않을 수 있으니 참고해주세요.'}
            status={'info'}
            hasStatusIcon
            hasBg={false}
            hasLinkIcon={false}
          />
        </div>
      </BottomDrawer.Basic.Contents>
    </BottomDrawer.Basic>
  );
};
